import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, CheckCircle, AlertCircle, Smartphone, QrCode, ArrowRight, Shield, Activity } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DeviceClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceClaimed: () => void;
}

const DeviceClaimModal: React.FC<DeviceClaimModalProps> = ({ isOpen, onClose, onDeviceClaimed }) => {
  const [imei, setImei] = useState('863');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [claimedDevice, setClaimedDevice] = useState<{ device_id: string; device_nickname: string } | null>(null);

  const handleClaimDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      // Remove the 863 prefix if present and send only the 12-digit IMEI
      let deviceId = imei.trim();
      if (deviceId.startsWith('863') && deviceId.length === 15) {
        deviceId = deviceId.substring(3);
      }
      
      // Call the claim_device function
      const { data, error: claimError } = await supabase
        .rpc('claim_device', { 
          p_device_id: deviceId,
          p_claim_method: 'manual_imei'
        });

      if (claimError) throw claimError;

      if (data && data.length > 0 && data[0].success) {
        setSuccess(true);
        setClaimedDevice({
          device_id: data[0].device_id,
          device_nickname: data[0].device_nickname || `Device ${data[0].device_id}`
        });
        
        // Notify parent component
        setTimeout(() => {
          onDeviceClaimed();
          handleClose();
        }, 2000);
      } else {
        setError(data?.[0]?.message || 'Failed to claim device');
      }
    } catch (err: any) {
      console.error('Error claiming device:', err);
      setError(err.message || 'An error occurred while claiming the device');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setImei('863');
    setError('');
    setSuccess(false);
    setClaimedDevice(null);
    onClose();
  };

  if (!isOpen) return null;

  // Use Portal to render outside the map's DOM tree
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in modal-overlay">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl transform transition-all animate-slide-up modal-content">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-traqpack-yellow/20 rounded-2xl flex items-center justify-center">
              <Package className="w-6 h-6 text-traqpack-yellow" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Claim Your Device</h2>
              <p className="text-sm text-gray-600">Add a TraqPack device to your account</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-xl"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 animate-bounce">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Device Claimed!</h3>
            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Smartphone className="w-5 h-5 text-gray-600" />
                <p className="text-lg font-semibold text-gray-900">
                  {claimedDevice?.device_nickname}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                IMEI: {claimedDevice?.device_id}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Activity className="w-4 h-4 animate-pulse" />
              <span>Redirecting to your devices...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Claim Methods */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button className="p-4 bg-gray-50 hover:bg-gray-100 rounded-xl border-2 border-gray-200 hover:border-traqpack-yellow transition-all group">
                  <QrCode className="w-8 h-8 text-gray-600 group-hover:text-traqpack-yellow mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Scan QR Code</p>
                  <p className="text-xs text-gray-500 mt-1">Coming soon</p>
                </button>
                <button className="p-4 bg-traqpack-yellow/10 rounded-xl border-2 border-traqpack-yellow transition-all">
                  <Smartphone className="w-8 h-8 text-traqpack-yellow mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Enter IMEI</p>
                  <p className="text-xs text-gray-600 mt-1">Manual entry</p>
                </button>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-gray-900 mb-2">Where to find your IMEI?</p>
                    <ul className="space-y-1.5 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>On the device label (back or bottom)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>On the original packaging box</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>In your purchase confirmation email</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleClaimDevice} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Enter Device IMEI Number
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={imei}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      // Ensure the 863 prefix stays
                      if (value.length < 3) {
                        setImei('863');
                      } else if (!value.startsWith('863')) {
                        setImei('863' + value.substring(3));
                      } else {
                        setImei(value);
                      }
                    }}
                    className="w-full pl-12 pr-14 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 text-lg font-medium transition-all"
                    maxLength={15}
                    pattern="[0-9]{15}"
                    required
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <span className={`text-sm font-medium ${imei.length === 15 ? 'text-green-600' : 'text-gray-400'}`}>
                      {imei.length}/15
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                  <Shield className="w-4 h-4" />
                  <span>Your device IMEI is encrypted and secure</span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 animate-shake">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading || imei.length !== 15}
                  className="w-full bg-black text-traqpack-yellow font-bold py-4 px-6 rounded-xl hover:bg-gray-900 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3 shadow-lg"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-traqpack-yellow"></div>
                      <span>Verifying Device...</span>
                    </>
                  ) : (
                    <>
                      <span>Claim Device</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full mt-3 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
                <button type="button" onClick={() => {}} className="hover:text-gray-700 transition-colors">Need help?</button>
                <span className="text-gray-300">•</span>
                <button type="button" onClick={() => {}} className="hover:text-gray-700 transition-colors">Contact support</button>
                <span className="text-gray-300">•</span>
                <button type="button" onClick={() => {}} className="hover:text-gray-700 transition-colors">User guide</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DeviceClaimModal;